import { ArrowRight } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { Card } from '@/components/Card';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import type { SocialDealCopy } from '@/types/deal';

export type SocialCreateCardProps = {
  specs: NonNullable<SocialDealCopy['create_specs']>;
  /** "Read the playbook" row; omitted when there's nothing to open or advance. */
  onReadBrief?: () => void;
  testID?: string;
};

/** "Let’s create": the deal's spec rows, filming tips, and the brief. Server copy. */
export function SocialCreateCard({ specs, onReadBrief, testID }: SocialCreateCardProps) {
  const rows = specs.rows ?? [];
  const tips = specs.tips ?? [];

  return (
    <Card variant="flat" padding={0} testID={testID}>
      {rows.map((row, i) => (
        <View key={row.label} style={[styles.row, i > 0 && styles.divided]}>
          <Text variant="caption" color="textMuted">
            {row.label}
          </Text>
          <Text variant="bodyBold" color="deepGreen">
            {row.value}
          </Text>
        </View>
      ))}
      {tips.length > 0 ? (
        <View style={[styles.tips, rows.length > 0 && styles.divided]}>
          {tips.map((tip, i) => (
            <Text key={i} variant="caption" color="text">
              {`• ${tip}`}
            </Text>
          ))}
        </View>
      ) : null}
      {onReadBrief ? (
        <Pressable
          onPress={onReadBrief}
          accessibilityRole="button"
          style={({ pressed }) => [styles.row, styles.divided, pressed && styles.pressed]}
          testID={testID ? `${testID}-brief` : undefined}
        >
          <Text variant="bodyBold" color="deepGreen">
            Read the playbook
          </Text>
          <View style={styles.arrow}>
            <ArrowRight size={14} color={colors.frost} strokeWidth={2.5} />
          </View>
        </Pressable>
      ) : null}
    </Card>
  );
}

const ARROW = 24;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
  },
  divided: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  tips: {
    gap: spacing.xs,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
  },
  pressed: {
    opacity: 0.6,
  },
  arrow: {
    width: ARROW,
    height: ARROW,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.deepGreen,
  },
});
